import { useCallback, useEffect, useState } from 'react';
import { Logo } from '../app/Shell';

type Overview = {
  generatedAt: string;
  submissions: { total: number; byResult: Record<string, number> };
  reservations: {
    byState: Record<string, number>;
    releasesByReason: Record<string, number>;
    verificationPending: { id: string; company_key: string; expires_at: string }[];
  };
  occupancy: { id: string; programme: string; number: number; capacity: number; status: string; active: number }[];
  hiresByProgramme: { programme: string; smes: number; hires: number }[];
  routing: {
    rules: Record<string, number>;
    waitlistReasons: Record<string, number>;
    discrepancies: { id: string; reservation_id: string; created_at: string; company_name: string }[];
  };
  sources: { source: string | null; campaign: string | null; submissions: number; eligible: number }[];
  delivery: { kind: string; status: string; n: number; retrying: number }[];
  recent: {
    id: string;
    created_at: string;
    result: string;
    programme: string | null;
    company_name: string;
    routing_rule: string;
    sync_status: string | null;
    booking_observed_at: string | null;
  }[];
};
type FailingIntent = {
  id: string;
  kind: string;
  status: string;
  attempts: number;
  max_attempts: number;
  last_error: string | null;
  last_error_at: string | null;
  submission_id: string | null;
};
type Analytics =
  { status: 'ok'; rows: { event: string; step: string; source: string; events: number; sessions: number }[] } | { status: 'unavailable'; reason: string };

async function get<T>(path: string): Promise<T> {
  const response = await fetch(path, { headers: { accept: 'application/json' } });
  if (!response.ok) throw new Error(String(response.status));
  return (await response.json()) as T;
}

const cell: React.CSSProperties = { padding: '8px 10px', borderBottom: '1px solid rgba(0,0,0,.08)', textAlign: 'left', fontSize: 14, verticalAlign: 'top' };

function Table({ caption, head, rows }: { caption: string; head: string[]; rows: (string | number | null)[][] }) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12 }}>
      <caption style={{ textAlign: 'left', fontWeight: 600, fontSize: 15, padding: '8px 0' }}>{caption}</caption>
      <thead>
        <tr>
          {head.map((h) => (
            <th key={h} scope="col" style={{ ...cell, fontWeight: 600, fontSize: 13, color: '#4A4A4A' }}>
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr>
            <td style={cell} colSpan={head.length}>
              None
            </td>
          </tr>
        ) : (
          rows.map((row, i) => (
            <tr key={i}>
              {row.map((value, j) => (
                <td key={j} style={{ ...cell, fontVariantNumeric: 'tabular-nums' }}>
                  {value ?? '·'}
                </td>
              ))}
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div style={{ border: '1px solid rgba(0,0,0,.08)', borderRadius: 14, padding: '14px 16px', background: '#fff' }}>
      <div className="s13 mute">{label}</div>
      <div className="num" style={{ fontSize: 26, fontWeight: 700, marginTop: 4 }}>
        {value}
      </div>
    </div>
  );
}

/** Internal operations view. Served only behind Cloudflare Access (worker/admin/access.ts). */
export function Admin() {
  const [overview, setOverview] = useState<Overview | null>(null);
  const [intents, setIntents] = useState<FailingIntent[]>([]);
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [o, i, a] = await Promise.all([
        get<Overview>('/api/admin/overview'),
        get<FailingIntent[]>('/api/admin/intents'),
        get<Analytics>('/api/admin/analytics'),
      ]);
      setOverview(o);
      setIntents(i);
      setAnalytics(a);
      setError(null);
    } catch (e) {
      setError(`Could not load the dashboard (${(e as Error).message}).`);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function replay(id: string) {
    await fetch(`/api/admin/intents/${encodeURIComponent(id)}/replay`, { method: 'POST' });
    await load();
  }

  const r = overview?.submissions.byResult ?? {};
  const s = overview?.reservations.byState ?? {};
  return (
    <div style={{ background: '#F7F8FA', minHeight: '100vh', fontFamily: 'var(--font-ui)' }}>
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 24px',
          background: '#fff',
          borderBottom: '1px solid rgba(0,0,0,.08)',
        }}
      >
        <Logo />
        <span className="s14 mute">Funded hiring operations</span>
      </header>
      <main style={{ maxWidth: 1200, margin: '0 auto', padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
          <h1 className="disp" style={{ fontSize: 26 }}>
            Funnel and places
          </h1>
          <button type="button" className="back" onClick={() => void load()}>
            Refresh
          </button>
        </div>
        {error ? (
          <p role="alert" className="notice" style={{ marginTop: 16 }}>
            {error}
          </p>
        ) : null}
        {overview ? (
          <>
            <p className="s13 mute" style={{ marginTop: 4 }}>
              Operational figures from D1, complete for every completed check. Generated {new Date(overview.generatedAt).toLocaleString('en-GB')}.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 12, marginTop: 16 }}>
              <Stat label="Checks completed" value={overview.submissions.total} />
              <Stat label="Eligible" value={r.eligible ?? 0} />
              <Stat label="Check needed" value={r.check_needed ?? 0} />
              <Stat label="Waitlist" value={r.waitlist ?? 0} />
              <Stat label="Places reserved" value={s.reserved ?? 0} />
              <Stat label="Terms signed (confirmed)" value={s.confirmed ?? 0} />
              <Stat label="Verification pending" value={s.verification_pending ?? 0} />
              <Stat label="Released" value={s.released ?? 0} />
            </div>
            <Table
              caption="Cohort occupancy (SMEs)"
              head={['Programme', 'Cohort', 'Active', 'Capacity', 'Status']}
              rows={overview.occupancy.map((c) => [c.programme, c.number, c.active, c.capacity, c.status])}
            />
            <Table
              caption="Planned hires by programme (active places)"
              head={['Programme', 'SMEs', 'Planned hires']}
              rows={overview.hiresByProgramme.map((h) => [h.programme, h.smes, h.hires])}
            />
            <Table caption="Releases by reason" head={['Reason', 'Count']} rows={Object.entries(overview.reservations.releasesByReason)} />
            <Table
              caption="Verification pending (HubSpot could not confirm at expiry)"
              head={['Reservation', 'Company', 'Expired']}
              rows={overview.reservations.verificationPending.map((v) => [v.id, v.company_key, v.expires_at])}
            />
            <Table
              caption="Delivery status"
              head={['Kind', 'Status', 'Count', 'Retrying']}
              rows={overview.delivery.map((d) => [d.kind, d.status, d.n, d.retrying])}
            />
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12 }}>
              <caption style={{ textAlign: 'left', fontWeight: 600, fontSize: 15, padding: '8px 0' }}>Failing or dead deliveries (HubSpot, email)</caption>
              <thead>
                <tr>
                  {['Kind', 'Status', 'Attempts', 'Last error', 'Submission', ''].map((h) => (
                    <th key={h} scope="col" style={{ ...cell, fontWeight: 600, fontSize: 13, color: '#4A4A4A' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {intents.length === 0 ? (
                  <tr>
                    <td style={cell} colSpan={6}>
                      None
                    </td>
                  </tr>
                ) : (
                  intents.map((i) => (
                    <tr key={i.id}>
                      <td style={cell}>{i.kind}</td>
                      <td style={cell}>{i.status}</td>
                      <td style={cell}>
                        {i.attempts} of {i.max_attempts}
                      </td>
                      <td style={cell}>{i.last_error}</td>
                      <td style={cell}>{i.submission_id}</td>
                      <td style={cell}>
                        {i.status === 'dead' ? (
                          <button type="button" className="back" onClick={() => void replay(i.id)}>
                            Replay
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            <Table caption="Routing rules fired" head={['Rule', 'Count']} rows={Object.entries(overview.routing.rules)} />
            <Table caption="Waitlist reasons" head={['Reason', 'Count']} rows={Object.entries(overview.routing.waitlistReasons)} />
            <Table
              caption="Repeat submissions routed differently from their existing place"
              head={['Submission', 'Reservation', 'Company', 'When']}
              rows={overview.routing.discrepancies.map((d) => [d.id, d.reservation_id, d.company_name, d.created_at])}
            />
            <Table
              caption="Completed checks by source"
              head={['UTM source', 'Campaign', 'Checks', 'Eligible']}
              rows={overview.sources.map((x) => [x.source, x.campaign, x.submissions, x.eligible])}
            />
            <Table
              caption="Recent submissions"
              head={['When', 'Result', 'Programme', 'Company', 'Rule', 'HubSpot sync', 'Booked']}
              rows={overview.recent.map((x) => [
                new Date(x.created_at).toLocaleString('en-GB'),
                x.result,
                x.programme,
                x.company_name,
                x.routing_rule,
                x.sync_status,
                x.booking_observed_at ? 'yes' : 'no',
              ])}
            />
            <h2 className="disp" style={{ fontSize: 20, marginTop: 32 }}>
              Visits and drop-off (consented visitors only)
            </h2>
            <p className="s13 mute" style={{ marginTop: 4 }}>
              Counted only for visitors who accepted analytics cookies, over the last 30 days. These are not complete traffic totals.
            </p>
            {analytics?.status === 'ok' ? (
              <Table
                caption="Events"
                head={['Event', 'Step', 'Source', 'Events', 'Sessions']}
                rows={analytics.rows.map((x) => [x.event, x.step, x.source, x.events, x.sessions])}
              />
            ) : (
              <p className="notice" style={{ marginTop: 12 }}>
                Analytics unavailable: {analytics?.status === 'unavailable' ? analytics.reason : 'loading'}.
              </p>
            )}
          </>
        ) : null}
      </main>
    </div>
  );
}
